/**
 * PAST PAPERS — 503 real MMD questions, each showing what the Academy can honestly do with it.
 *
 * Founder, 2026-08-11: *"the 503 question bank can be a great resource."* It was sitting on disk read
 * only by three gate scripts, as a regression workload. This is the face for it.
 *
 * ── ANSWERING IS NOT OFFERING, AND THIS PANEL NEARLY SHIPPED SAYING IT WAS ─────────────────────
 * The first build showed one merged number and called 97 questions "worked on our ship". Measured,
 * all 97 of those regenerated questions ask the SAME thing — final drafts and fluid GM after a
 * loading — while the examiner's originals ask about list during a lift, discharge, free surface,
 * dock water and weight shift. A drill is not an answer, and presenting it as one is the padding
 * this codebase refuses everywhere else.
 *
 * So the two claims are shown in SEPARATE rows, never added together:
 *
 *   we ANSWER      cited 78 · partly 89 · not answered 336
 *   we can OFFER   drill on our ship 97 · drawing to compare 33
 *
 * 206 questions have neither, and they stay on the surface. A gap a candidate can SEE is one he can
 * prepare for elsewhere; a hidden one is a trap set for the exam hall. FP-018 pointed at ourselves.
 *
 * The questions are DG Shipping examination record and are served VERBATIM — never reworded,
 * merged, summarised or completed, because a question reworded at ingestion can never be quoted
 * afterwards.
 */
import { useState } from 'react';
import { Value, useWidgetData } from '../../../packages/ankr-glass/src/react.jsx';
import { plan } from '../../../packages/ankr-vahana/src/plan.mjs';
import { computed, cited, refused } from '../../../packages/ankr-provenance/src/provenance.mjs';
import { API } from './surfaces.jsx';

const W = {
  index: { slug: 'past-papers', layout: 'auto-table',
           wire: { kind: 'rest', route: '/api/pastpapers', method: 'GET', field: 'papers' } },
  paper: { slug: 'past-paper', layout: 'auto-table',
           wire: { kind: 'rest', route: '/api/pastpaper', method: 'GET', field: 'items' } },
};

/* TWO VOCABULARIES, BECAUSE THEY ARE TWO CLAIMS. What we can SAY about a question is not what we
   can GIVE a candidate to work, and the first build of this panel merged them into one flattering
   number. A drill on our own hull is genuinely useful and is genuinely NOT an answer. */
const ANSWER = {
  cited:   { label: 'answered, cited', chip: 'g-chip-cited' },
  partial: { label: 'partly answered', chip: 'g-chip-cited' },
  none:    { label: 'not answered', chip: 'g-chip-null' },
};
const OFFER = {
  drill:   { label: 'drill on our ship', chip: 'g-chip-computed' },
  drawing: { label: 'drawing to compare', chip: 'g-chip-computed' },
};

function Tally({ tally, total }) {
  const a = tally?.answer || {}, o = tally?.offer || {};
  return (
    <>
      <div className="g-label" style={{ marginTop: 'var(--g-4)' }}>What we can answer</div>
      <div className="pp-tally">
        {Object.entries(ANSWER).map(([k, meta]) => (
          <div key={k} className="pp-tally-cell">
            <div className={`g-chip ${meta.chip}`}>{meta.label}</div>
            <div className="pp-tally-n g-mono">{a[k] || 0}</div>
          </div>
        ))}
        {total ? <div className="pp-tally-cell"><div className="g-label">questions</div>
          <div className="pp-tally-n g-mono">{total}</div></div> : null}
      </div>
      {/* Kept in a SEPARATE row, deliberately. Putting these beside the answers would invite a
          reader to add them, and they are not the same claim. */}
      <div className="g-label" style={{ marginTop: 'var(--g-4)' }}>
        What we can give you to work instead — not an answer
      </div>
      <div className="pp-tally">
        {Object.entries(OFFER).map(([k, meta]) => (
          <div key={k} className="pp-tally-cell">
            <div className={`g-chip ${meta.chip}`}>{meta.label}</div>
            <div className="pp-tally-n g-mono">{o[k] || 0}</div>
          </div>
        ))}
      </div>
    </>
  );
}

/**
 * SELF-MARKING. He ticks what he actually drew; we add it up. The app does not mark his sketch and
 * must never imply it did — the examiner's scheme is quoted, the judgement is his. That is also why
 * it works at sea: a scheme is data (11 KB for all 202), a marking engine is not.
 */
function SelfMark({ atlas }) {
  const [ticked, setTicked] = useState({});
  const [marking, setMarking] = useState(false);
  const all = [...(atlas.members || []),
               ...(atlas.not_in_model || []).map((m) => ({ label: m.label, marks: null, why: m.why }))];
  const got = all.reduce((t, m) => t + (ticked[m.label] ? (m.marks || 0) : 0), 0);

  if (!marking) {
    return (
      <button className="g-btn pp-step-go" onClick={() => setMarking(true)}>
        Mark your own sketch against this scheme
      </button>
    );
  }
  return (
    <div className="pp-selfmark">
      <div className="g-label">Tick what you actually drew and labelled</div>
      <div className="pp-ticks">
        {all.map((m) => (
          <label key={m.label} className={`pp-tick ${ticked[m.label] ? 'on' : ''}`}>
            <input type="checkbox" checked={Boolean(ticked[m.label])}
              onChange={(e) => setTicked((t) => ({ ...t, [m.label]: e.target.checked }))} />
            <span>{m.label}</span>
            <b className="g-mono">{m.marks ?? '·'}</b>
          </label>
        ))}
      </div>
      {/* HIS claim, phrased as his. "You scored", never "we marked". */}
      <p className="g-body pp-score">
        <b>You scored {got} of {atlas.sheet_marks}</b> on your own marking, against the examiner&apos;s
        scheme for this sheet.
      </p>
      <p className="g-body g-dim pp-note">
        Nothing here read your drawing. This is the scheme quoted and your own honest count — which is
        what an oral is. Members shown with · carry marks on the sheet that the atlas cannot model, so
        judge those from the sheet itself.
      </p>
    </div>
  );
}

/** One question, verbatim, with BOTH claims kept apart underneath it. */
function Q({ q }) {
  const [open, setOpen] = useState(false);
  const a = ANSWER[q.answer] || ANSWER.none;
  const o = OFFER[q.offer];
  const d = q.offer_detail || {};
  return (
    <article className={`pp-q pp-${q.answer === 'none' && !o ? 'unheld' : q.answer === 'none' ? 'offered' : q.answer}`}>
      <button className="pp-q-head" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <span className="pp-q-n g-mono">{q.part}{q.number}</span>
        <span className="pp-q-topic">{String(q.topic || '').replace(/-/g, ' ')}</span>
        <span className={`g-chip ${a.chip}`}>{a.label}</span>
        {o ? <span className={`g-chip ${o.chip}`}>{o.label}</span> : null}
        <span className="pp-q-marks g-dim">{q.marks} marks</span>
      </button>

      {/* VERBATIM. The question is the examiner's, and it is shown as he set it. */}
      <p className="g-body pp-q-text">{q.text}</p>

      {open && (
        <div className="pp-q-body">
          {/* WHAT WE CAN SAY ABOUT THIS QUESTION — always first, including when it is nothing. */}
          {q.answer === 'cited' && (
            <>
              <p className="g-body">{q.answer_why}</p>
              <p className="g-body g-dim pp-note">
                Ask it in <b>Learn</b> to read the cited answer, and check the citation rather than us.
              </p>
            </>
          )}
          {q.answer === 'partial' && (
            <Value of={refused(`${q.parts_grounded} of ${q.parts} parts ground in the corpus — the `
              + 'remainder are a declared gap, not an answer being withheld')} />
          )}
          {q.answer === 'none' && <Value of={refused(q.answer_why)} />}

          {/* WHAT WE CAN OFFER INSTEAD — plainly marked as a substitute, never as the answer. */}
          {q.offer === 'drill' && (() => {
            const m = d.matched_drill;
            const kindWord = { 'list-during-lift': 'maximum list during a derrick lift',
              list: 'angle of list from a transverse shift', discharge: 'discharge',
              shift: 'longitudinal weight shift', load: 'loading' }[d.wants] || d.wants;
            return (
              <div className="pp-offer">
                <div className="g-label">
                  {m ? `Instead — a ${kindWord} drill of the same kind, on our own ship`
                     : 'Instead — a drill on our own ship, and it is NOT the kind he set'}
                </div>
                {/* WHEN THE DRILL IS THE WRONG KIND, THAT IS THE FIRST THING SAID. */}
                {!m && (
                  <Value of={refused(`he asked a ${kindWord} question and we have no engine for that `
                    + 'kind yet — what follows is a loading drill, which is useful practice but is '
                    + 'not the problem he set')} />
                )}
                {m ? (
                  <>
                    <p className="g-body g-dim">
                      <b>This is not the question above.</b> Same kind of problem, set on
                      {' '}<b>{String(m.ship_name || m.ship || '').toUpperCase()}</b> from her own
                      hydrostatics. Work it, then check:
                    </p>
                    <div className="pp-answer">
                      {Number.isFinite(m.list_deg) && <Value of={computed(`list ${m.list_deg}°`)} />}
                      {Number.isFinite(m.new_draft_fwd_m) && <Value of={computed(`drafts F/A ${m.new_draft_fwd_m} / ${m.new_draft_aft_m} m`)} />}
                      {Number.isFinite(m.change_of_trim_cm) && <Value of={computed(`trim ${m.change_of_trim_cm} cm`)} />}
                      {Number.isFinite(m.parallel_sinkage_cm) && <Value of={computed(`sinkage ${m.parallel_sinkage_cm} cm`)} />}
                      {Number.isFinite(m.GM_fluid_m) && <Value of={computed(`GM ${m.GM_fluid_m} m`)} />}
                      {Number.isFinite(m.virtual_rise_of_G_m) && <Value of={computed(`rise of G ${m.virtual_rise_of_G_m} m`)} />}
                      {Number.isFinite(m.FWA_mm) && <Value of={computed(`FWA ${m.FWA_mm} mm`)} />}
                      {Number.isFinite(m.DWA_mm) && <Value of={computed(`dock water allowance ${m.DWA_mm} mm`)} />}
                      {Number.isFinite(m.new_draft_m) && <Value of={computed(`new draft ${m.new_draft_m} m`)} />}
                      {Number.isFinite(m.move_distance_m) && <Value of={computed(`move it ${Math.abs(m.move_distance_m)} m`)} />}
                      {Number.isFinite(m.resulting_draft_fwd_m) && <Value of={computed(`resulting F/A ${m.resulting_draft_fwd_m} / ${m.resulting_draft_aft_m} m`)} />}
                    </div>
                    {m.why && <p className="g-body g-dim pp-note">{m.why}</p>}
                    {/* Each drill type carries its own teaching point — the direction she moves, the
                        reason the allowance exists, why trim splits about F and not midships. */}
                    {m.direction && <p className="g-body g-dim pp-note">{m.direction}</p>}
                    {m.note && <p className="g-body g-dim pp-note">{m.note}</p>}
                    {/* The formula's own limit, where it applies. */}
                    {m.small_angle_warning && (
                      <div style={{ marginTop: 'var(--g-2)' }}><Value of={refused(m.small_angle_warning)} /></div>
                    )}
                    {m.sinkage_note && <p className="g-body g-dim pp-note">{m.sinkage_note}</p>}
                  </>
                ) : (
                  <>
                    <p className="g-body pp-ours">{d.our_question}</p>
                    <div className="pp-answer">
                      {d.computed?.drafts_m && <Value of={computed(`drafts F/A ${d.computed.drafts_m.join(' / ')} m`)} />}
                      {Number.isFinite(d.computed?.GM_fluid_m) && <Value of={computed(`fluid GM ${d.computed.GM_fluid_m} m`)} />}
                    </div>
                  </>
                )}
                <p className="g-body g-dim pp-note">
                  Our own designed teaching hulls — the arithmetic is real, the vessel is ours, and it
                  is not an approved stability booklet.
                </p>
              </div>
            );
          })()}

          {q.offer === 'drawing' && (
            <div className="pp-offer">
              <div className="g-label">Instead — learn it in 3D, draw it in 2D, then explain it</div>
              <p className="g-body">
                We hold <b>{d.sheets_held}</b> sheet{d.sheets_held === 1 ? '' : 's'} of
                {' '}<b>{String(d.drawing_family || '').replace(/-/g, ' ')}</b>.
                {d.atlas ? <> The atlas carries this one as <b>{d.atlas.solids} named solids</b> compiled
                  from <b>{d.atlas.ship_name}</b>&apos;s own offsets — not a picture of a ship, her actual
                  structure.</> : null}
              </p>

              {d.atlas ? (
                <>
                  <ol className="pp-steps">
                    <li>
                      <b>Understand it in 3D.</b> Rotate the section and see why each member sits where
                      it does — a flat plate cannot show you that a hopper slopes to shed cargo.
                      {' '}<a className="g-btn pp-step-go" href={`/atlas?drawing=${encodeURIComponent(d.example)}`}
                             target="_blank" rel="noreferrer">Open the 3D atlas</a>
                    </li>
                    <li>
                      <b>Draw it in 2D.</b> This is the artefact an examiner marks — he does not mark a
                      rotation. Sketch it, photograph it, be examined on it.
                      {' '}<a className="g-btn pp-step-go" href={`/paper?id=${encodeURIComponent(d.example)}`}
                             target="_blank" rel="noreferrer">Open the drawing paper</a>
                    </li>
                    <li>
                      <b>Explain it aloud.</b> Name every member and say what it does. The list below is
                      not our invention — it is the sheet&apos;s own mark scheme. The whole sheet is
                      worth <b>{d.atlas.sheet_marks} marks</b> across <b>{d.atlas.members_marked}</b>
                      {' '}marked members, and the atlas carries solids for
                      {' '}<b>{d.atlas.members_in_atlas}</b> of them.
                    </li>
                  </ol>
                  {/* THE MARK SCHEME, QUOTED. Showing what each member is worth is the difference
                      between practising and guessing at what the examiner wants. */}
                  <div className="pp-members">
                    {d.atlas.members.map((m) => (
                      <span key={m.label} className="pp-member">
                        {m.label}<b className="g-mono">{m.marks}</b>
                      </span>
                    ))}
                  </div>
                  {/* THE MEMBERS THE MODEL DOES NOT HOLD, NAMED. Showing eleven of sixteen silently
                      would read as the whole drawing — the atlas author's own warning, and the first
                      draft of this panel walked straight into it. Each says WHY it has no solid. */}
                  {d.atlas.not_in_model?.length ? (
                    <div className="pp-notmodelled">
                      <div className="g-label">
                        Marked on the sheet, no solid in the atlas — draw these from the sheet
                      </div>
                      {d.atlas.not_in_model.map((m) => (
                        <p key={m.label} className="g-body">
                          <b>{m.label}</b> — {m.why}
                        </p>
                      ))}
                    </div>
                  ) : null}
                  <SelfMark atlas={d.atlas} />
                </>
              ) : (
                <Value of={refused('the atlas holds no solids for this drawing, so there is no 3D to '
                  + 'learn it from — sketch it against our sheet and compare')} />
              )}
            </div>
          )}
        </div>
      )}
    </article>
  );
}

export default function PastPapers() {
  const [key, setKey] = useState(null);
  const [filter, setFilter] = useState(null);

  const idx = useWidgetData(W.index, {}, { baseUrl: API, plan });
  const pap = useWidgetData(W.paper, key ? { key } : {}, { baseUrl: API, plan, enabled: Boolean(key) });

  if (idx.envelope) return <div className="g-glass shell-panel"><Value of={idx.envelope} /></div>;
  const data = idx.data || {};

  if (key) {
    const p = pap.data || {};
    /* The filter carries WHICH claim it names, because 'none' is a legal value of both and a merged
       parameter silently returned everything. */
    const items = (p.items || []).filter((q) => !filter
      || (filter.kind === 'answer' ? q.answer === filter.k : q.offer === filter.k));
    return (
      <div className="shell-col g-rise">
        <div className="g-glass shell-panel">
          <div className="pp-head">
            <div>
              <div className="g-label">MMD past paper · {p.session} {p.sitting !== '?' ? p.sitting : ''}</div>
              <h2 className="g-h2" style={{ marginTop: 'var(--g-2)' }}>
                {p.questions} questions · {p.marks} marks
              </h2>
            </div>
            <button className="g-btn" onClick={() => { setKey(null); setFilter(null); }}>All papers</button>
          </div>
          <Tally tally={p.tally} />
          <div className="shell-picker" style={{ marginTop: 'var(--g-3)' }}>
            <button className={`g-btn ${!filter ? 'g-btn-primary' : ''}`} onClick={() => setFilter(null)}>
              Everything
            </button>
            {[...Object.entries(ANSWER).map(([k, m]) => ['answer', k, m, p.tally?.answer?.[k] || 0]),
              ...Object.entries(OFFER).map(([k, m]) => ['offer', k, m, p.tally?.offer?.[k] || 0])]
              .map(([kind, k, meta, n]) => (
                <button key={`${kind}-${k}`}
                  className={`g-btn ${filter?.kind === kind && filter?.k === k ? 'g-btn-primary' : ''}`}
                  onClick={() => setFilter({ kind, k })} disabled={!n}>
                  {meta.label} {n ? `(${n})` : ''}
                </button>
              ))}
          </div>
        </div>

        <div className="g-glass shell-panel">
          {pap.loading ? <div className="gl-empty g-body">Opening the paper…</div>
            : !items.length ? <div className="gl-empty g-body">No question in this paper is in that state.</div>
              : <div className="pp-list">{items.map((q) => <Q key={q.id} q={q} />)}</div>}
        </div>
      </div>
    );
  }

  return (
    <div className="shell-col g-rise">
      <div className="g-glass shell-panel">
        <div className="g-label">Past papers</div>
        <h2 className="g-h2" style={{ margin: 'var(--g-2) 0 var(--g-3)' }}>
          Real MMD questions, and what we can do with each
        </h2>
        {/* THE HONEST HEADLINE, in the engine's own words. 503 is the size of the bank; the number
            that matters to a candidate is what he can work. */}
        {data.honesty && <p className="g-body">{data.honesty}</p>}
        <Tally tally={data.tally} total={data.total} />
        {data.provenance && <p className="g-body g-dim pp-note">{data.provenance}</p>}
      </div>

      <div className="g-glass shell-panel">
        <div className="g-label">{(data.papers || []).length} papers</div>
        <div className="pp-papers">
          {(data.papers || []).map((p) => (
            <button key={p.key} className="pp-paper" onClick={() => setKey(p.key)}>
              <span className="pp-paper-name">{p.session}{p.sitting !== '?' ? ` · ${p.sitting}` : ''}</span>
              <span className="pp-paper-n g-dim">{p.questions} questions</span>
              <span className="pp-paper-bar" aria-hidden="true">
                {/* The bar shows what we can ANSWER — the claim that matters for a paper. */}
                {Object.entries(ANSWER).map(([k, meta]) => {
                  const n = p.tally?.answer?.[k] || 0;
                  return n ? <i key={k} className={`pp-seg pp-seg-${k}`}
                               style={{ flexGrow: n }} title={`${n} ${meta.label}`} /> : null;
                })}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
